import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Api from "../../services/Api";
import { getLanguage } from "../../lang/helpers";
import {BookedMeeting} from "../../interfaces/common";
import CustomButton from "../../components/CustomButton";
import DotsPopup from "../../components/Popup/DotsPopup";
import {openMeeting} from "../../helpers/open_meeting"
import {dateFormatDate, dateFormatHourMinute, isSameDate} from "../../helpers/dateFunctions";
import { CalendarWeek as CalendarIcon, Clock } from "flowbite-react-icons/outline";
import BookMeetingFree from "../../components/BookMeeting/BookMeetingFree";
import CancelMeeting from "../../components/CancelMeeting/CancelMeeting";
import AlertIcon from "../../components/Icons/AlertIcon";

interface CoachStartInterface {
  name: string
  meetings: Array<BookedMeeting>
  onboarded: string | null
  availability: 0
  completeProfiles: 0
}


const CoachHome: React.FC = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [data, setData] = useState<CoachStartInterface>({
    name: "None", meetings: [], onboarded: null, availability: 0, completeProfiles: 0
  });
  const [selectedMeeting, setSelectedMeeting] = useState<BookedMeeting|null>(null)
  const [showCancelMeeting, setShowCancelMeeting] = useState<boolean>(false)
  const [showChangeMeeting, setShowChangeMeeting] = useState<boolean>(false)
  const wide = window.innerWidth > 1100;

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (loaded) {
      getData();
    }
  }, [lang]);

  const getData = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    let url = 'coach/start'
    if (timezone) {
      url += '?tz=' + timezone
    }
    api.get(url, ["coach_not_found"]).then(response => {
      if (response.json.error === 'not_applied' || response.json.error === 'not_onboarded') {
        navigate('/application')
        return
      }
      for (let m of response.json.meetings as Array<BookedMeeting>) {
        m.startDate = new Date(m.start)
      }
      setData(response.json)
      setLoaded(true);
    }).catch(error => {
      if (error.isExpectedError) {
        alert(error.json.message)
      } else {
        alert('Unknown error (http status ' + error.response.status + ')')
      }
    });
  };

  const calculateMeetingsToday = () => {
    let midnight = new Date()
    midnight.setHours(23)
    midnight.setMinutes(59)
    let meetingsToday = 0
    for (const m of data.meetings as Array<BookedMeeting>) {
      if (m.startDate < midnight) {
        meetingsToday++
      }
    }

    if (meetingsToday === 0) {
      return t("no-meetings-today");
    } else if (meetingsToday === 1) {
      return t("one-meetings-today");
    } else {
      return t("you-have") + " " + meetingsToday + " " +t("meetings-today")
    }
  }

  const calculateEndTime = (start: Date, l: number) => {
    return new Date(start.getTime() + l * 60000)
  }

  const clickChangeMeeting = (meeting: BookedMeeting) => {
    setSelectedMeeting(meeting)
    setShowChangeMeeting(true)
  }

  const clickCancelMeeting = (meeting: BookedMeeting) => {
    setSelectedMeeting(meeting)
    setShowCancelMeeting(true)
  }

  const closeCancelMeeting = (changed: boolean) => {
    setShowCancelMeeting(false)
    if (changed) {
      getData()
    }
  }

  const closeChangeMeeting = (changed: boolean) => {
    setShowChangeMeeting(false)
    if (changed) {
      getData()
    }
  }

  const goToMeeting = (meetingId: string) => {
    if (api.apiToken) {
      openMeeting(meetingId, api.apiToken, "coach");
    } else {
      alert("Fatal error - missing token");
    }

  }

  const showNextMeetings = () => {
    if (data.meetings.length < 1) {
      return <></>
    } else {
      const now = new Date()
      const meeting: BookedMeeting = data.meetings[0]
      const endTime = calculateEndTime(meeting.startDate, meeting.length)
      const meetingToday = isSameDate(now, meeting.startDate)
      const moreMeetings: Array<BookedMeeting> = []
      for (let i = 1; i < data.meetings.length; i++) {
        moreMeetings.push(data.meetings[i])
      }

      const popItems = [
        {title: t('to-meeting'), click: () => goToMeeting(meeting.id)},
        {title: t('change-meeting'), click: () => clickChangeMeeting(meeting)},
        {title: t('cancel-meeting'), click: () => clickCancelMeeting(meeting)}
      ]

      return (
        <>
          <div className="rounded-lg mt-6 p-4 w-full bg-neutral-800">
            <p className="font-medium">{meetingToday ? t('today') : t('next-meeting')}</p>
            <div className="mt-4">
              <p className="text-xl z-link"
                 onClick={() => navigate('/clients/' + meeting.clientId)}>
                {meeting.firstName} {meeting.lastName}
              </p>
              <div className="flex justify-between items-center">
                <div className="flex items-center mt-2">
                  {
                    !meetingToday &&
                      <>
                          <CalendarIcon className="icon-lg" aria-hidden="true"/>
                          <span className="mx-2">{dateFormatDate(meeting.startDate, false, lang)}</span>
                      </>
                  }
                  <Clock className="icon-lg"/>
                  <span className="ml-2 text-sm font-medium">
                {dateFormatHourMinute(meeting.startDate)} - {dateFormatHourMinute(endTime)}
              </span>
                </div>
                <DotsPopup items={popItems}/>
              </div>

              <div className="mt-4 mb-2 flex justify-evenly">
                <CustomButton
                  onClick={() => goToMeeting(meeting.id)}
                  className="w-full"
                  buttonType="outlineWhiteBase"
                >
                  {t('to-meeting')}
                </CustomButton>
              </div>
            </div>

            {
              moreMeetings.filter(m => isSameDate(m.startDate, meeting.startDate))
                .map((m, i) => {
                  return(
                    <div key={"more_" + i} className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="text-sm z-link w-32"
                             onClick={() => navigate(`/clients/${m.clientId}`)}>
                          {m.firstName} {m.lastName}
                        </div>
                        <div className="flex items-center ml-2">
                          <Clock className="ml-2 picture-xs"/>
                          <span className="ml-2 text-sm font-medium">
                              {dateFormatHourMinute(m.startDate)}
                            &ndash;
                            {dateFormatHourMinute(calculateEndTime(m.startDate, m.length))}
                            </span>
                        </div>
                      </div>

                      <DotsPopup items={[
                        {title: t('to-meeting'), click: () => goToMeeting(m.id)},
                        {title: t('change-booking'), click: () => clickChangeMeeting(m)},
                        {title: t('cancel-booking'), click: () => clickCancelMeeting(m)}
                      ]}/>
                    </div>
                  )
                })
            }
          </div>

        </>
      )
    }
  }

  if (!loaded) {
    return (
      <>
        <h2>Loading...</h2>
      </>
    )
  }

  if (data.onboarded === null) {
    navigate('/onboarding')
  }

  if (showChangeMeeting && selectedMeeting) {
    return (
      <>
        <BookMeetingFree meeting={selectedMeeting} clientId={selectedMeeting.clientId}
                         close={closeChangeMeeting} lang={lang}/>
      </>
    )
  }

  return (
    <div className="main-content">
      {
        (showCancelMeeting && selectedMeeting) &&
          <CancelMeeting lang={lang} meeting={selectedMeeting}
                         close={closeCancelMeeting}/>
      }
      <h1 className="text-3xl mt-4 font-medium text-white">
        {t("hi")} {data.name}
      </h1>
      {
        data.completeProfiles < 1 &&
          <div className="rounded-lg mt-6 p-4 w-full bg-neutral-800 border border-cherry-500">
              <div className="flex items-center">
                  <AlertIcon aria-label="Alert" className="text-cherry-500!"/>
                  <p className="ml-2 font-medium text-white">
                    {t("complete-profiles")}
                  </p>
              </div>

              <p className="mt-4">
                {
                  lang === 'sv' ?
                    "Färdigställ din coachprofil så att klienter kan välja dig."
                    :
                    "Complete your coach profile so clients can choose you."
                }
              </p>
              <CustomButton onClick={() => navigate('/profile')} className="mt-4"
                            buttonType="dangerBase">
                {t("go-to-profile")}
              </CustomButton>
          </div>
      }

      {
        data.availability < 1 &&
          <div className="rounded-lg mt-6 p-4 w-full bg-neutral-800 border border-cherry-500">
              <div className="flex items-center">
                  <AlertIcon aria-label="Alert" className="text-cherry-500!"/>
                  <p className="ml-2 font-medium text-white">
                    {t("add-more-availability")}
                  </p>
              </div>

              <p className="mt-4">
                {
                  lang === 'sv' ?
                    "Lägg till mötestider så att dina klienter kan boka möten med dig."
                    :
                    "Add your meeting times so clients can book meetings with you."
                }
              </p>

              <CustomButton onClick={() => navigate('/availability')} className="mt-4"
                            buttonType="dangerBase">
                {t("go-to-availability")}
              </CustomButton>
          </div>
      }

      <p className="mt-4">{calculateMeetingsToday()}</p>
      {showNextMeetings()}
    </div>
  );
};

export default CoachHome;
